import { useState, useSyncExternalStore, type KeyboardEvent } from 'react';
import { Alert, Box, Button, Chip, TextField, Typography } from '@mui/material';
import { formatTimeMs } from '@/common/utils/audioPlayerUtils';
import type { TimeSource } from './timeSource';

interface CommentComposerProps {
  /** Live playhead; read narrowly so only the time labels re-render per tick. */
  timeSource: TimeSource;
  /** Resolves on success; rejects/throws on failure. */
  onSubmit: (body: string, atMs: number) => Promise<unknown>;
  /** Track length in ms; posted atMs is clamped to [0, maxMs]. */
  maxMs?: number;
}

function LiveTime({
  timeSource,
  frozenMs,
}: {
  timeSource: TimeSource;
  frozenMs: number | null;
}) {
  const live = useSyncExternalStore(timeSource.subscribe, timeSource.get);
  return <>{formatTimeMs(frozenMs ?? live)}</>;
}

export function CommentComposer({
  timeSource,
  onSubmit,
  maxMs,
}: CommentComposerProps) {
  const [body, setBody] = useState('');
  const [frozenMs, setFrozenMs] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const canSubmit = body.trim().length > 0 && !submitting;

  function handleChange(next: string) {
    if (next.length === 0) setFrozenMs(null);
    else if (body.length === 0 && frozenMs === null) {
      setFrozenMs(timeSource.get());
    }
    setBody(next);
  }

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const rounded = Math.max(0, Math.round(frozenMs ?? timeSource.get()));
      const atMs = maxMs === undefined ? rounded : Math.min(rounded, maxMs);
      await onSubmit(body.trim(), atMs);
      setBody('');
      setFrozenMs(null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setSubmitting(false);
    }
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void handleSubmit();
    }
  }

  return (
    <Box data-cy="comment-composer" sx={{ my: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Chip
          size="small"
          data-cy="comment-composer-time"
          label={<LiveTime timeSource={timeSource} frozenMs={frozenMs} />}
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        />
        {frozenMs !== null ? (
          <Button
            size="small"
            data-cy="comment-follow-playhead"
            onClick={() => setFrozenMs(null)}
          >
            Follow playhead
          </Button>
        ) : (
          <Typography variant="caption" color="text.secondary">
            Click the waveform to set the time
          </Typography>
        )}
      </Box>
      <TextField
        fullWidth
        multiline
        minRows={2}
        placeholder="Add a comment"
        value={body}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={handleKeyDown}
        inputProps={{ maxLength: 2000, 'aria-label': 'comment' }}
      />
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error.message}
        </Alert>
      )}
      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          size="small"
          data-cy="comment-submit"
          disabled={!canSubmit}
          onClick={() => void handleSubmit()}
        >
          Comment at <LiveTime timeSource={timeSource} frozenMs={frozenMs} />
        </Button>
      </Box>
    </Box>
  );
}
